import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Sparkles,
  Send,
  ArrowRight,
  X,
  ShieldCheck,
  CheckCircle2,
  Clock,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from "@/App";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { siteContact } from "@/config/site";

import PageHero from "@/components/shared/PageHero";
import Section from "@/components/shared/Section";
import SectionHeader from "@/components/shared/SectionHeader";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import Eyebrow from "@/components/shared/Eyebrow";

const FAQS = [
  {
    q: "How quickly will I hear back?",
    a: "We usually respond within 24 business hours to initial enquiries. If your request is time-sensitive, please call us.",
  },
  {
    q: "Do you sign NDAs?",
    a: "Yes — we're happy to sign mutual non-disclosure agreements before engaging with sensitive data.",
  },
  {
    q: "Can you work with regulated data?",
    a: "Yes — we have experience building secure data platforms and governance for financial and healthcare clients.",
  },
  {
    q: "What industries do you serve?",
    a: "We work with financial services, healthcare, retail, logistics and enterprise SaaS companies.",
  },
];

const HOW_WE_HELP = [
  "AI Strategy & Roadmapping",
  "ML Engineering & MLOps",
  "Data Platforms & Analytics",
  "Generative & Conversational AI",
];

/* ---------- Forms ---------- */

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Please fill required fields");
    try {
      setLoading(true);
      await axios.post(`${API}/contact/send`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message || "Contact via website",
        source: "Contact Form",
        website: form.website,
      });
      toast.success("Thanks! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
    } catch (err) {
      toast.error("Failed to send. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5 block">
            Name *
          </label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-[color:var(--input-bg)] border-[color:var(--border-default)] focus:border-[color:var(--brand-primary)] h-11 rounded-xl"
            placeholder="Jane Cooper"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5 block">
            Email *
          </label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="bg-[color:var(--input-bg)] border-[color:var(--border-default)] focus:border-[color:var(--brand-primary)] h-11 rounded-xl"
            placeholder="jane@company.com"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5 block">
          Phone
        </label>
        <Input
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="bg-[color:var(--input-bg)] border-[color:var(--border-default)] focus:border-[color:var(--brand-primary)] h-11 rounded-xl"
          placeholder="+91 99999 99999"
        />
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5 block">
          Message
        </label>
        <Textarea
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="bg-[color:var(--input-bg)] border-[color:var(--border-default)] focus:border-[color:var(--brand-primary)] rounded-xl resize-none"
          rows={4}
          placeholder="Tell us about your project, goals, and timeline..."
        />
      </div>

      {/* Honeypot */}
      <div className="absolute left-[-10000px] top-auto w-1 h-1 overflow-hidden" aria-hidden="true">
        <Input
          name="website"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="flex items-center justify-between gap-4 pt-2">
        <p className="text-xs text-[var(--text-muted)] inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-[var(--brand-primary-light)]" />
          Your data is encrypted in transit
        </p>
        <Button type="submit" disabled={loading} className="btn-primary rounded-full px-6 h-11">
          <span className="inline-flex items-center gap-2">
            {loading ? "Sending..." : "Send message"}
            {!loading && <Send className="w-4 h-4" />}
          </span>
        </Button>
      </div>
    </form>
  );
}

function DiscoveryCallForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    date: "",
    time: "",
    timezone: "IST (UTC+05:30)",
    agenda: "",
    website: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.date || !form.time || !form.timezone) {
      return toast.error("Please fill required fields");
    }
    try {
      setLoading(true);
      await axios.post(`${API}/contact/send`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: "Discovery call request",
        source: "Discovery Call",
        company: form.company,
        role: form.role,
        date: form.date,
        time: form.time,
        timezone: form.timezone,
        agenda: form.agenda,
        website: form.website,
      });
      toast.success("Thanks! We'll confirm a time shortly.");
      setForm({
        name: "", email: "", phone: "", company: "", role: "",
        date: "", time: "", timezone: "IST (UTC+05:30)", agenda: "", website: "",
      });
    } catch {
      toast.error("Failed to schedule. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-[color:var(--input-bg)] border-[color:var(--border-default)] focus:border-[color:var(--brand-primary)] h-10 rounded-xl";
  const labelCls = "text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5 block";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Name *</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Company</label>
          <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Role</label>
          <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Timezone *</label>
          <select
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            className="w-full rounded-xl border border-[color:var(--border-default)] bg-[color:var(--input-bg)] px-3 h-10 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]"
          >
            <option>IST (UTC+05:30)</option>
            <option>UTC</option>
            <option>PT (UTC-08:00)</option>
            <option>ET (UTC-05:00)</option>
            <option>GMT (UTC+00:00)</option>
            <option>CET (UTC+01:00)</option>
            <option>SGT (UTC+08:00)</option>
            <option>AEST (UTC+10:00)</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Preferred Date *</label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Preferred Time *</label>
          <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={inputCls} />
        </div>
      </div>

      <div>
        <label className={labelCls}>Agenda</label>
        <Textarea
          value={form.agenda}
          onChange={(e) => setForm({ ...form, agenda: e.target.value })}
          className="bg-[color:var(--input-bg)] border-[color:var(--border-default)] focus:border-[color:var(--brand-primary)] rounded-xl resize-none"
          rows={3}
          placeholder="What would you like to discuss?"
        />
      </div>

      {/* Honeypot */}
      <div className="absolute left-[-10000px] w-1 h-1 overflow-hidden" aria-hidden="true">
        <Input
          name="website"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={loading} className="btn-primary rounded-full px-6 h-11">
          <span className="inline-flex items-center gap-2">
            {loading ? "Scheduling..." : "Request a call"}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </span>
        </Button>
      </div>
    </form>
  );
}

/* ---------- Page ---------- */

const Contact = () => {
  const [isDiscoveryOpen, setIsDiscoveryOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setIsDiscoveryOpen(false);
    if (isDiscoveryOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDiscoveryOpen]);

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = isDiscoveryOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isDiscoveryOpen]);

  return (
    <div className="min-h-screen text-[var(--text-primary)]">
      <PageHero
        eyebrow="Contact"
        eyebrowIcon={Sparkles}
        title={
          <>
            Let's build the <span className="gradient-text">future together</span>
          </>
        }
        description={
          <>
            Whether you need a strategic roadmap, a pilot, or full production deployment — reach out and we'll connect
            you with the right team. Typical response time:{" "}
            <strong className="text-[var(--text-primary)]">within 24 hours</strong>.
          </>
        }
      />

      {/* Contact methods */}
      <Section size="sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Reveal variant="up">
            <GlowCard className="p-7 h-full flex flex-col">
              <div className="feature-icon mb-4">
                <Mail className="w-5 h-5" />
              </div>
              <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">General Enquiries</p>
              <a
                href={`mailto:${siteContact.email}`}
                className="font-semibold mt-1 break-words text-[var(--text-primary)] hover:text-[var(--brand-primary-light)] transition-colors"
              >
                {siteContact.email}
              </a>
              <p className="text-xs text-[var(--text-muted)] mt-auto pt-4">
                We reply to emails within one business day.
              </p>
            </GlowCard>
          </Reveal>

          <Reveal variant="up" delay={0.06}>
            <GlowCard className="p-7 h-full flex flex-col">
              <div className="feature-icon mb-4">
                <Phone className="w-5 h-5" />
              </div>
              <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Call Us</p>
              <a
                href={`tel:${siteContact.phone}`}
                className="font-semibold mt-1 text-[var(--text-primary)] hover:text-[var(--brand-primary-light)] transition-colors"
              >
                {siteContact.phone}
              </a>
              <p className="text-xs text-[var(--text-muted)] mt-auto pt-4 inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Mon–Fri · 9:00 AM – 6:00 PM IST
              </p>
            </GlowCard>
          </Reveal>

          <Reveal variant="up" delay={0.12}>
            <GlowCard variant="liquid" className="aurora p-7 h-full flex flex-col overflow-hidden">
              <div className="relative z-10 flex flex-col h-full">
                <div className="feature-icon mb-4">
                  <Calendar className="w-5 h-5" />
                </div>
                <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Schedule a Meeting</p>
                <p className="font-semibold mt-1">Book a Discovery Call</p>
                <Button
                  type="button"
                  onClick={() => setIsDiscoveryOpen(true)}
                  className="btn-primary rounded-full px-6 w-full mt-4 h-10"
                >
                  <span className="inline-flex items-center gap-2 text-sm">
                    Pick a time <ArrowRight className="w-4 h-4" />
                  </span>
                </Button>
                <p className="text-xs text-[var(--text-muted)] mt-3">
                  We'll prepare a tailored agenda for your call.
                </p>
              </div>
            </GlowCard>
          </Reveal>
        </div>
      </Section>

      {/* Main content */}
      <Section>
        <div className="grid lg:grid-cols-5 gap-6 items-start max-w-6xl mx-auto">
          <Reveal variant="up" className="lg:col-span-2 space-y-5">
            <GlowCard className="p-7">
              <Eyebrow icon={Sparkles} className="mb-3">How we help</Eyebrow>
              <h3 className="font-display font-semibold text-xl mb-3">
                We turn AI initiatives into <span className="gradient-text">measurable outcomes.</span>
              </h3>
              <p className="text-[var(--text-secondary)] text-sm mb-5 leading-relaxed">
                We help teams adopt AI responsibly — from scoping and prototypes to model delivery and operationalization.
                Domain knowledge, secure data engineering, and production-grade ML pipelines on every engagement.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {HOW_WE_HELP.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                    <CheckCircle2 className="w-4 h-4 text-[var(--brand-primary-light)] flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </GlowCard>

            <GlowCard className="p-7">
              <Eyebrow icon={MapPin} className="mb-3">Office</Eyebrow>
              <h3 className="font-display font-semibold text-lg mb-3">Head Office</h3>
              <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-5">
                {siteContact.address.full}
              </p>
              <div className="rounded-2xl overflow-hidden border border-[color:var(--border-default)]">
                <img
                  src="https://images.pexels.com/photos/259924/pexels-photo-259924.jpeg?auto=compress&cs=tinysrgb&w=1200"
                  alt="Office location"
                  loading="lazy"
                  className="w-full h-44 object-cover"
                />
              </div>
            </GlowCard>
          </Reveal>

          <Reveal variant="up" delay={0.1} className="lg:col-span-3">
            <GlowCard variant="liquid" className="aurora p-7 sm:p-8 overflow-hidden">
              <div className="relative z-10">
                <Eyebrow icon={Send} className="mb-3">Send a message</Eyebrow>
                <h3 className="font-display font-semibold text-2xl mb-2">
                  Tell us about your <span className="gradient-text">project.</span>
                </h3>
                <p className="text-[var(--text-secondary)] text-sm mb-7">
                  Fill out the form and our team will reach out within 24 hours.
                </p>
                <ContactForm />
              </div>
            </GlowCard>

            <p className="text-xs text-[var(--text-muted)] mt-4">
              <strong className="text-[var(--text-primary)]">Privacy:</strong> Your data will only be used to respond to your request. See our{" "}
              <Link to="/privacy" className="text-[var(--brand-primary-light)] hover:underline">
                Privacy Policy
              </Link>.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <SectionHeader
          eyebrow="FAQ"
          eyebrowIcon={Sparkles}
          title={<>Frequently asked <span className="gradient-text">questions</span></>}
        />
        <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} variant="up" delay={(i % 2) * 0.06}>
              <GlowCard className="p-6 h-full">
                <p className="font-semibold mb-2">{f.q}</p>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{f.a}</p>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Discovery call modal */}
      <AnimatePresence>
        {isDiscoveryOpen && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-24 pb-6 sm:pt-28"
            role="dialog"
            aria-modal="true"
            aria-labelledby="discovery-call-title"
          >
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-md"
              onClick={() => setIsDiscoveryOpen(false)}
              aria-hidden="true"
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-2xl max-h-[calc(100vh-8rem)] overflow-y-auto liquid-glass rounded-3xl p-7 shadow-2xl"
            >
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <Eyebrow icon={Calendar} className="mb-2">Schedule</Eyebrow>
                  <h3 id="discovery-call-title" className="font-display font-bold text-2xl">
                    Book a Discovery Call
                  </h3>
                  <p className="text-[var(--text-secondary)] text-sm mt-1">
                    Share a few details and your preferred time. We'll confirm and send a calendar invite.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsDiscoveryOpen(false)}
                  className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-white/5 rounded-full p-2 transition-colors flex-shrink-0"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <DiscoveryCallForm />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Contact;
